import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes, ratio, LoadingSpinner, pitchTypeDisplayName } from 'yankees-react-utils';
import styled from 'styled-components';
import useConfig from '../../../../utils/useConfig';
import StatTooltip from '../../../../components/StatTooltip';

const StyledWrapper = styled.div`
    text-align: center;
    .high-usage, .low-usage {
        font-weight: bold !important;
    }
    .high-usage {
        background-color: #AACCFF !important;
    }
    .low-usage {
        background-color: #F2B9B4 !important;
    }
    .class15 {
        background-color: #FFFF77 !important;
    }
    .class45 {
        background-color: #FFCC00 !important;
    }
    .class75 {
        background-color: #F2B9B4 !important;
    }
    .dark-red-cell {
        background-color: rgb(250, 128, 117) !important;
        font-weight: bold;
    }
    .red-cell {
        background-color: rgb(242, 185, 180) !important;
        font-weight: bold;
    }
    .dark-blue-cell {
        background-color: rgb(120, 172, 250) !important;
        font-weight: bold;
    }
`;

const getCellShadingClass = (row, val, fxtype, zScoreData, key) => {
    if (row.count / row.countTotal < 0.1) return '';

    const zscore = zScoreData.find((z) => z.pitchtype === fxtype);
    if (!zscore) return '';

    const avg = zscore[`${key}_avg`];
    const sd = zscore[`${key}_sd`];
    if (val >= (avg + (2 * sd))) {
        return 'dark-red-cell';
    }
    if (val >= (avg + sd)) {
        return 'red-cell';
    }
    if (val <= (avg - (2 * sd))) {
        return 'dark-blue-cell';
    }
    if (val <= (avg - sd)) {
        return 'blue-cell';
    }

    return '';
};

const getUsageClass = (pPct, lgPct, threshold, includeLowUsage) => {
    let usageClass;
    if (pPct >= (lgPct + threshold)) {
        usageClass = 'high-usage';
    } else if (includeLowUsage && pPct <= (lgPct - threshold)) {
        usageClass = 'low-usage';
    }
    return usageClass;
};

const kzoneAccuracy = (usage, lgavgkzon, kAccuracy, lgavgKey, fxtype) => {
    if (usage < 0.1) return '';

    let accuracyClass = '';
    const lgAvg = lgavgkzon.find((avg) => avg.fxtype === fxtype || (avg.fxtype === null && !fxtype));
    if (kAccuracy > 0 && lgAvg && lgAvg[`t${lgavgKey}`] > 0) {
        const pEst = lgAvg[`kzon${lgavgKey}`] / lgAvg[`t${lgavgKey}`];
        const stdDev = Math.sqrt(pEst * (1 - pEst) / 200);
        accuracyClass = getUsageClass(kAccuracy, pEst, 2 * stdDev, true);
    }
    return accuracyClass;
};

const pitchBreak = (usage, pitchcharseries, pitch, breakX) => {
    if (usage < 0.1) return '';

    const BREAK_BUFFER_ZONE = 1.5; // anything in the buffer (vs avg) is not touched
    const BREAK_RANGE = 3;
    const targetPitchChar = pitchcharseries.find((x) => x.pitchtype === pitch);
    let valUse = 0;
    if (targetPitchChar) {
        if (breakX) {
            valUse = targetPitchChar.diffx;
        } else {
            valUse = targetPitchChar.diffz;
        }

        const abs = Math.abs(valUse);
        if (abs <= BREAK_BUFFER_ZONE) {
            return '';
        }
        if (abs <= (BREAK_RANGE + BREAK_BUFFER_ZONE)) {
            return 'class15'; // yellow
        }
        if (abs <= ((BREAK_RANGE * 2) + BREAK_BUFFER_ZONE)) {
            return 'class45'; // orange
        }

        return 'class75'; // red
    }

    return '';
};

const getPitchCharSeries = (pitches, pitchCharacteristics, throws, pitcherId, yearId, currentYear) => {
    let pcuse = pitchCharacteristics.pitches;
    let applyYearFilter = false;
    if (throws) {
        pcuse = pcuse.filter((x) => x.pitcherid === throws); // prefer the pitches averaged for their arm type
    } else {
        pcuse = pcuse.filter((x) => x.pitcherid !== pitcherId); // backup is things that aren't the specific player
        applyYearFilter = true; // in these cases we need to filter out some old year data that could be in the set (below)
    }
    if (applyYearFilter && yearId === -1) {
        pcuse = pcuse.filter((y) => y.yearid === currentYear || y.yearid === currentYear - 1);
    } else if (applyYearFilter) {
        pcuse = pcuse.filter((y) => y.yearid === yearId);
    }
    const pitchtypes = new Set(pitches.map((x) => x.fxtype));
    pcuse = pcuse.filter((y) => pitchtypes.has(y.pitchtype));
    const pitchCharSeries = [];
    pcuse.forEach((pitch) => {
        const pitchDiffX = pitchCharacteristics?.movementnotes?.find((x) => x.pitchtype === pitch.pitchtype)?.diffx;
        const pitchDiffZ = pitchCharacteristics?.movementnotes?.find((x) => x.pitchtype === pitch.pitchtype)?.diffz;
        pitchCharSeries.push({
            pitchtype: pitch.pitchtype,
            diffx: pitchDiffX,
            diffz: pitchDiffZ,
        });
    });

    return pitchCharSeries;
};

const prepareRowsBreakTable = (repertoire, pitches, translate) => {
    const returnValue = [];
    repertoire.filter((type) => type.fxtype !== 1).forEach((type) => {
        const targetPitches = pitches.filter((x) => x.fxtype === type.fxtype && x.fxsubtype === type.fxsubtype);
        returnValue.push({
            type: pitchTypeDisplayName(type, true, true, translate),
            fxtype: type.fxtype,
            fxsubtype: type.fxsubtype,
            // pitches: targetPitches,
            veloMin: type.minvelo,
            veloMax: type.maxvelo,
            veloAvg: type.avgvelo,
            count: type.pitches,
            countTotal: type.pitches_total,
            inzone: type.inzone,
            outzone: type.outzone,
            msinzone: type.msinzone,
            swinzone: type.swinzone,
            breakx54Avg: targetPitches.reduce((total, next) => total + next.breakx54, 0) / targetPitches.length,
            breakz54Avg: targetPitches.reduce((total, next) => total + next.breakz54, 0) / targetPitches.length,
            gb: type.gb,
            fb: type.fb,
            pitches: type.pitches,
            pstrike: type.pstrike,
            spinrate: ratio(targetPitches.reduce((total, next) => total + next.spinrate, 0), targetPitches.filter((x) => x.spinrate).length),
            vertapprangle: ratio(targetPitches.reduce((total, next) => total + next.vertapprangle, 0), targetPitches.filter((x) => x.vertapprangle).length),
            chase: ratio(targetPitches.reduce((total, next) => total + next.ch, 0), targetPitches.reduce((total, next) => total + next.outz, 0)),
        });
    });

    return returnValue;
};
const PitchBreakTable = ({ isLoading, zScoreData, lgavgkzon, pitches, pitchCharacteristics, repertoire, throws, pitcherId, yearId }) => {
    const { t: translate } = useTranslation();
    const { CurrentYear } = useConfig();

    const pitchCharSeries = getPitchCharSeries(pitches, pitchCharacteristics, throws, pitcherId, yearId, CurrentYear);

    const columns = [
        { id: 'type', title: translate('sections.opponent.player.type'), headerClass: 'left', cellClass: () => 'left', type: columnTypes.TEXT },
        {
            id: 'velo',
            title: translate('sections.reports.pitcher.abbr-velo'),
            statistic: 'VeloRange',
            transform: (row) => `${row.veloMin}-${row.veloMax} (${row.veloAvg})`,
            cellClass: (r) => getCellShadingClass(r, r.veloAvg, r.fxtype, zScoreData.results, 'initspeed54'),
            type: columnTypes.NODE,
        },
        {
            id: 'pit%',
            title: translate('pitcher-overview.pitch-break.pit-percent'),
            statistic: 'PitchTypePercent',
            transform: (row) => ratio(row.count, row.countTotal),
            type: columnTypes.PERCENT,
            cellClass: (r) => getCellShadingClass(r, ratio(r.count, r.countTotal), r.fxtype, zScoreData.results, 'usage'),
        },
        {
            id: 'pfxkzon%',
            title: translate('sections.reports.pitcher.kzon-pct'),
            statistic: 'KZon',
            cellClass: (r, v) => kzoneAccuracy(ratio(r.count, r.countTotal), lgavgkzon, v, 'pit', r.fxtype),
            transform: (row) => ratio(row.pstrike, row.pitches),
            type: columnTypes.PERCENT,
        },
        {
            id: 'mszn%',
            title: translate('pitcher-overview.pitch-break.ms-inz'),
            statistic: 'MsInZon',
            transform: (row) => ratio(row.msinzone, row.swinzone),
            type: columnTypes.PERCENT,
            cellClass: (r) => getCellShadingClass(r, ratio(r.msinzone, r.swinzone), r.fxtype, zScoreData.results, 'msinz'),
        },
        {
            id: 'chase',
            title: translate('sections.reports.pitcher.chase'),
            statistic: 'Ch',
            type: columnTypes.PERCENT,
            cellClass: (r) => getCellShadingClass(r, r.chase, r.fxtype, zScoreData.results, 'ch'),
        },
        {
            id: 'breakx54Avg',
            title: translate('pitcher-overview.pitch-break.horiz-break'),
            statistic: 'BreakX',
            cellClass: (r) => pitchBreak(ratio(r.count, r.countTotal), pitchCharSeries, r.fxtype, true),
            precision: 1,
        },
        {
            id: 'breakz54Avg',
            title: translate('pitcher-overview.pitch-break.vert-break'),
            statistic: 'BreakZ',
            cellClass: (r) => pitchBreak(ratio(r.count, r.countTotal), pitchCharSeries, r.fxtype, false),
            precision: 1,
        },
        {
            id: 'spinrate',
            title: translate('sections.reports.pitcher.spin-rate'),
            statistic: 'SpinRate',
            precision: 0,
            cellClass: (r) => getCellShadingClass(r, r.spinrate, r.fxtype, zScoreData.results, 'spinrate'),
        },
        {
            id: 'vertapprangle',
            title: translate('sections.opponent.player.abbr-app-ang'),
            statistic: 'VertApprAngle',
            precision: 1,
            cellClas: (r) => getCellShadingClass(r, r.vertapprangle, r.fxtype, zScoreData.results, 'vertapprangle'),

        },
        {
            id: 'gbfb',
            title: translate('baseball.abbr-gb-fb'),
            statistic: 'GBperFB',
            transform: (row) => (Number.isNaN(row.gb / row.fb) || row.fb === 0 ? '--' : `${(row.gb / row.fb).toFixed(1)}`),
            type: columnTypes.NODE,
            cellClass: (r) => (Number.isNaN(r.gb / r.fb) || r.fb === 0 ? '' : getCellShadingClass(r, r.gb / r.fb, r.fxtype, zScoreData.results, 'gb_fb')),
        },
    ];

    if (isLoading) return <div className="py-5 text-center"><LoadingSpinner /></div>;
    return (
        <StyledWrapper>
            <DataTable
                columns={columns}
                rows={prepareRowsBreakTable(repertoire, pitches, translate)}
                statTooltip={StatTooltip}
            />
        </StyledWrapper>
    );
};

export default PitchBreakTable;
